import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Brain, Cloud, Repeat, BadgeCheck, GraduationCap, Puzzle, HeartHandshake, Check,
  Search, PenTool, Rocket, Activity,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

interface ServiceDef {
  id: string;
  icon: React.ElementType;
  accent: string;
  related: string;
  group: 'consulting' | 'support';
}

const services: ServiceDef[] = [
  { id: 'ai-consulting', icon: Brain, accent: 'from-cyan-400 to-blue-500', related: '/products/zimbra-sovereign-ai', group: 'consulting' },
  { id: 'managed-cloud', icon: Cloud, accent: 'from-sky-400 to-cyan-500', related: '/solutions', group: 'consulting' },
  { id: 'migration', icon: Repeat, accent: 'from-orange-400 to-rose-500', related: '/products/exchange-to-zimbra', group: 'consulting' },
  { id: 'fcshare', icon: BadgeCheck, accent: 'from-blue-400 to-sky-500', related: '/products/fcshare', group: 'consulting' },
  { id: 'training', icon: GraduationCap, accent: 'from-emerald-400 to-teal-500', related: '/training', group: 'support' },
  { id: 'integration', icon: Puzzle, accent: 'from-teal-400 to-emerald-500', related: '/products/ldap-zimbra-provisioning', group: 'support' },
  { id: 'support', icon: HeartHandshake, accent: 'from-amber-400 to-orange-500', related: '/contact', group: 'support' },
];

const stats = [
  { id: 'services', value: '7' },
  { id: 'countries', value: '4' },
  { id: 'sla', value: '99.9%' },
  { id: 'support', value: '24/7' },
] as const;

const stepIcons = [Search, PenTool, Rocket, Activity];

const ServicesPage = () => {
  const { t } = useTranslation('editor');
  const steps = t('servicesPage.process.steps', { returnObjects: true });
  const stepList = Array.isArray(steps) ? (steps as { title: string; description: string }[]) : [];

  const renderService = (s: ServiceDef, i: number) => {
    const Icon = s.icon;
    const base = `servicesPage.items.${s.id}`;
    const features = t(`${base}.features`, { returnObjects: true });
    const featureList = Array.isArray(features) ? (features as string[]) : [];
    return (
      <motion.article
        key={s.id}
        id={s.id}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ delay: (i % 2) * 0.08 }}
        className="group relative scroll-mt-40 rounded-3xl p-px bg-gradient-to-br from-white/15 via-white/5 to-white/10 hover:from-cyan-300/50 hover:to-emerald-300/40 transition-colors duration-500"
      >
        <div className="relative h-full rounded-3xl bg-slate-900/90 backdrop-blur-xl p-8 overflow-hidden">
          <div className={`pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br ${s.accent} opacity-10 blur-3xl transition-opacity duration-500 group-hover:opacity-25`} />
          <div className="relative flex items-start gap-4 mb-5">
            <div className={`flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br ${s.accent} flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}>
              <Icon className="h-5 w-5 text-slate-950" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold text-white">{t(`${base}.title`)}</h3>
              <p className="text-sm text-cyan-200/80 mt-1">{t(`${base}.tagline`)}</p>
            </div>
          </div>
          <p className="relative text-slate-300 leading-relaxed mb-6">{t(`${base}.description`)}</p>
          <ul className="relative grid sm:grid-cols-2 gap-3 mb-8">
            {featureList.map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-slate-200 rounded-xl bg-white/[0.04] border border-white/5 p-3">
                <Check className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>
          <div className="relative flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 hover:shadow-[0_0_32px_-6px_rgba(34,211,238,0.9)] transition-shadow"
            >
              {t('servicesPage.requestQuote')}
              <ArrowRight className="h-4 w-4" />
            </Link>
            {s.related !== '/contact' && (
              <Link
                to={s.related}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white border border-white/15 hover:border-cyan-300/50 hover:bg-white/[0.06] transition-colors"
              >
                {t(`${base}.link`)}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </motion.article>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-70" />
        <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[900px] h-[480px] rounded-full bg-gradient-to-r from-blue-600/30 via-cyan-500/25 to-emerald-500/20 blur-[120px] animate-aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
              <span className="text-cyan-200 text-sm font-semibold tracking-wide">{t('servicesPage.badge')}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              <span className="text-gradient animate-shimmer">{t('servicesPage.title')}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">{t('servicesPage.subtitle')}</p>
          </motion.div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {stats.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.08 }}
                className="glass-card rounded-2xl p-5"
              >
                <div className="font-display text-3xl font-bold text-white">{s.value}</div>
                <div className="text-xs text-slate-400 mt-1">{t(`servicesPage.stats.${s.id}`)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="sticky top-[136px] z-30 border-y border-white/5 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 overflow-x-auto no-scrollbar">
          <span className="text-xs uppercase tracking-[0.18em] text-slate-500 whitespace-nowrap">{t('servicesPage.quickNav')}</span>
          {services.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="whitespace-nowrap px-4 py-1.5 rounded-full text-sm text-slate-300 border border-white/10 hover:text-white hover:border-cyan-300/50 hover:bg-white/[0.05] transition-colors"
            >
              {t(`servicesPage.items.${s.id}.title`)}
            </a>
          ))}
        </div>
      </div>

      {(['consulting', 'support'] as const).map((group) => (
        <section key={group} className="relative py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300/80 mb-8">
              {t(`servicesPage.groups.${group}`)}
            </h2>
            <div className="grid lg:grid-cols-2 gap-6">
              {services.filter((s) => s.group === group).map(renderService)}
            </div>
          </div>
        </section>
      ))}

      <section className="relative py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 mb-4">{t('servicesPage.process.title')}</h2>
            <p className="text-lg text-slate-500">{t('servicesPage.process.subtitle')}</p>
          </div>
          <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="hidden lg:block absolute top-8 left-[12%] right-[12%] h-px bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300" />
            {stepList.map((step, i) => {
              const StepIcon = stepIcons[i] ?? Search;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="relative text-center"
                >
                  <div className="relative mx-auto w-16 h-16 rounded-2xl bg-slate-950 flex items-center justify-center shadow-xl mb-5">
                    <StepIcon className="h-6 w-6 text-cyan-300" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-cyan-300 to-emerald-300 text-slate-950 text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-60" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[360px] rounded-full bg-gradient-to-r from-blue-600/30 via-cyan-500/30 to-emerald-500/20 blur-[100px]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mb-5">{t('servicesPage.cta.title')}</h2>
          <p className="text-lg text-slate-300 mb-10">{t('servicesPage.cta.subtitle')}</p>
          <Link
            to="/contact"
            className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 shadow-[0_0_40px_-8px_rgba(34,211,238,0.8)] hover:shadow-[0_0_56px_-6px_rgba(34,211,238,1)] transition-shadow"
          >
            {t('servicesPage.cta.button')}
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
